import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, fmtTime, type DeadLetter, type Subscription } from "../api";
import { useGroups, useLogGroups } from "../hooks";
import { useSession } from "../session";

function DeadLetters({ sub }: { sub: Subscription }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["dlq", sub.id],
    queryFn: () => api<{ dead_letters: DeadLetter[] }>(`/v1/subscriptions/${sub.id}/dead-letters`),
  });
  const replay = useMutation({
    mutationFn: () => api<{ replayed: number }>(`/v1/subscriptions/${sub.id}/dead-letters/replay`, { method: "POST", body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dlq", sub.id] }),
  });
  const items = q.data?.dead_letters ?? [];
  return (
    <div className="dlq">
      <h3>Dead letters ({items.length})</h3>
      {items.map((d) => (
        <details key={d.id}>
          <summary>{fmtTime(d.created_at)} · {d.attempts} attempts · {d.last_error ?? d.last_status}</summary>
          <pre className="fields">{JSON.stringify(d.payload, null, 2)}</pre>
        </details>
      ))}
      {items.length > 0 && (
        <button type="button" onClick={() => replay.mutate()} disabled={replay.isPending}>Replay all</button>
      )}
      {replay.data && <p role="status">Replayed {replay.data.replayed}.</p>}
    </div>
  );
}

export function SubscriptionsPage() {
  const { tenant } = useSession();
  const qc = useQueryClient();
  const groups = useGroups();
  const logGroups = useLogGroups();
  const [form, setForm] = useState({ name: "", group: "", log_group: "", pattern: "", types: ["log"], dest: "durable", url: "" });
  const [secret, setSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const subs = useQuery({
    queryKey: ["subscriptions", tenant],
    queryFn: () => api<{ subscriptions: Subscription[] }>("/v1/subscriptions").then((r) => r.subscriptions),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["subscriptions"] });
  const create = useMutation({
    mutationFn: () =>
      api<Subscription & { webhook_secret?: string }>("/v1/subscriptions", {
        method: "POST",
        body: {
          name: form.name, group: form.group || undefined, log_group: form.log_group || undefined,
          pattern: form.pattern, event_types: form.types,
          destination: form.dest === "webhook" ? { type: "webhook", url: form.url } : { type: "durable" },
        },
      }),
    onSuccess: (res) => {
      setSecret(res.webhook_secret ?? null);
      setError(null);
      setForm({ ...form, name: "" });
      refresh();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });
  const update = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api(`/v1/subscriptions/${id}`, { method: "PATCH", body: { status } }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/subscriptions/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const scope = (s: Subscription) => {
    const g = groups.data?.find((x) => x.id === s.group_id)?.name;
    const l = logGroups.data?.find((x) => x.id === s.log_group_id)?.name;
    return g ? (l ? `${g} / ${l}` : `${g} / *`) : "whole tenant";
  };
  const toggleType = (t: string) =>
    setForm((f) => ({ ...f, types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t] }));

  return (
    <section aria-labelledby="subs-title">
      <h1 id="subs-title">Subscriptions</h1>
      <form className="card" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
        <h2>New subscription</h2>
        <div className="row">
          <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
          <label>
            Group
            <select value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value, log_group: "" })}>
              <option value="">whole tenant</option>
              {groups.data?.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select>
          </label>
          <label>
            Log group
            <select value={form.log_group} disabled={!form.group} onChange={(e) => setForm({ ...form, log_group: e.target.value })}>
              <option value="">all</option>
              {logGroups.data?.filter((l) => l.group_name === form.group).map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
            </select>
          </label>
          <label className="grow">Pattern<input value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} /></label>
        </div>
        <fieldset className="row">
          <legend>Event types</legend>
          {["log", "error", "issue"].map((t) => (
            <label key={t} className="inline"><input type="checkbox" checked={form.types.includes(t)} onChange={() => toggleType(t)} /> {t}</label>
          ))}
        </fieldset>
        <fieldset className="row">
          <legend>Destination</legend>
          <label className="inline"><input type="radio" name="dest" checked={form.dest === "durable"} onChange={() => setForm({ ...form, dest: "durable" })} /> Durable (pull + ack)</label>
          <label className="inline"><input type="radio" name="dest" checked={form.dest === "webhook"} onChange={() => setForm({ ...form, dest: "webhook" })} /> Webhook</label>
          {form.dest === "webhook" && (
            <label className="grow">URL<input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" required /></label>
          )}
        </fieldset>
        <button type="submit" disabled={create.isPending || !form.types.length}>Create</button>
        {error && <p role="alert" className="error">{error}</p>}
        {secret && (
          <p role="status" className="secret">Webhook signing secret (shown once): <code>{secret}</code></p>
        )}
      </form>

      <table className="grid">
        <caption className="sr-only">Subscriptions</caption>
        <thead><tr><th scope="col">Name</th><th scope="col">Scope</th><th scope="col">Pattern</th><th scope="col">Types</th><th scope="col">Destination</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>
          {subs.data?.map((s) => (
            <tr key={s.id}>
              <td>{s.name}</td>
              <td className="small">{scope(s)}</td>
              <td><code>{s.pattern || "*"}</code></td>
              <td>{s.event_types.join(", ")}</td>
              <td className="small">{s.destination.type}{s.destination.url ? ` → ${s.destination.url}` : ""}</td>
              <td><span className={`badge st-${s.status}`}>{s.status}</span></td>
              <td className="row">
                <button type="button" onClick={() => update.mutate({ id: s.id, status: s.status === "active" ? "paused" : "active" })}>
                  {s.status === "active" ? "Pause" : "Resume"}
                </button>
                {s.destination.type === "webhook" && (
                  <button type="button" aria-expanded={open === s.id} onClick={() => setOpen(open === s.id ? null : s.id)}>Dead letters</button>
                )}
                <button type="button" className="danger" onClick={() => window.confirm(`Delete ${s.name}?`) && remove.mutate(s.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && subs.data?.find((s) => s.id === open) && <DeadLetters sub={subs.data.find((s) => s.id === open)!} />}
    </section>
  );
}
